import { Environments, PaySafeEnvironments } from '@monorepo/client-types';
import { FF_CACHE_INTERVAL } from '@monorepo/feature-flag/shared';
import { Application } from '@monorepo/types';
import { CoralogixDomain } from '@coralogix/browser';
import { Environment, EnvKeys } from './env-service.types';

class EnvironmentService {
  application = this.getEnvVariable<Application>(
    EnvKeys.PROJECT,
    Application.THE5ERS,
  );
  appEnv = this.getEnvVariable<Environments>(
    EnvKeys.APP_ENV,
    Environments.Development,
  );
  apiEndPoint = this.getEnvVariable(EnvKeys.API_END_POINT);
  awsClientId = this.getEnvVariable(EnvKeys.AWS_CLIENT_ID);
  awsRegion = this.getEnvVariable(EnvKeys.AWS_REGION);
  cdnBaseUrl = this.getEnvVariable(EnvKeys.CDN_BASE_URL);
  cloudinaryFolder = this.getEnvVariable(EnvKeys.CLOUDINARY_FOLDER);
  cognitoPoolName = this.getEnvVariable(EnvKeys.COGNITO_POOL_NAME);
  domainName = this.getEnvVariable(EnvKeys.DOMAIN_NAME);
  leaddynoPublicKey = this.getEnvVariable(EnvKeys.LEADDYNO_PUBLIC_KEY);
  pusherApiKey = this.getEnvVariable(EnvKeys.PUSHER_API_KEY);
  pusherCluster = this.getEnvVariable(EnvKeys.PUSHER_CLUSTER);
  gtmId = this.getEnvVariable(EnvKeys.GTM_ID);
  mixPanelToken = this.getEnvVariable(EnvKeys.MIXPANEL_TOKEN);
  mixPanelApiHost = this.getEnvVariable(EnvKeys.MIXPANEL_API_HOST);
  sentryDsn = this.getEnvVariable(EnvKeys.SENTRY_DSN);
  awsUserPoolDomain = this.getEnvVariable(EnvKeys.AWS_USER_POOL_DOMAIN);
  recaptchaSiteKey = this.getEnvVariable(EnvKeys.RECAPTCHA_SITE_KEY);
  gaId = this.getEnvVariable(EnvKeys.GA_ID);
  paysafePublicKey = this.getEnvVariable(EnvKeys.PAYSAFE_PUBLIC_KEY);
  paysafeEnv = this.getEnvVariable<PaySafeEnvironments>(
    EnvKeys.PAYSAFE_ENV,
    PaySafeEnvironments.Test,
  );
  isProduction = this.appEnv === Environments.Production;
  s3BaseUrl = this.getEnvVariable(EnvKeys.S3_BASE_URL);
  isS3BaseEnabled = this.getEnvVariable(EnvKeys.S3_BASE_ENABLED) === 'true';
  featureFlagApiUrl = this.getEnvVariable(EnvKeys.FEATURE_FLAG_API_HOST);
  featureFlagClientKey = this.getEnvVariable(EnvKeys.FEATURE_FLAG_CLIENT_KEY);
  hubNextBaseUrl = this.getEnvVariable(
    EnvKeys.HUB_NEXT_BASE_URL,
    'https://hub.the5ers.com/go/',
  );
  ffCacheInterval = Number(
    this.getEnvVariable(EnvKeys.FF_CACHE_INTERVAL, FF_CACHE_INTERVAL),
  );
  coralogixRumPublicKey = this.getEnvVariable(EnvKeys.CORALOGIX_RUM_PUBLIC_KEY);
  coralogixRumDomain = this.getEnvVariable<CoralogixDomain>(
    EnvKeys.CORALOGIX_RUM_DOMAIN,
    'EU2',
  );
  coralogixRumInstrumentations = {
    errors: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_ERRORS,
    ),
    fetch: this.isRumInstrumentationEnabled(EnvKeys.CORALOGIX_RUM_INSTR_FETCH),
    xhr: this.isRumInstrumentationEnabled(EnvKeys.CORALOGIX_RUM_INSTR_XHR),
    custom: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_CUSTOM,
    ),
    long_tasks: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_LONG_TASKS,
      false,
    ),
    resources: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_RESOURCES,
      false,
    ),
    interactions: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_INTERACTIONS,
      false,
    ),
    web_vitals: this.isRumInstrumentationEnabled(
      EnvKeys.CORALOGIX_RUM_INSTR_WEB_VITALS,
    ),
  };
  version = this.getEnvVariable<string>(EnvKeys.VERSION);
  /**
   * Returns the configuration object with all environment variables
   */
  get(): Environment {
    return {
      application: this.application,
      appEnv: this.appEnv,
      apiEndPoint: this.apiEndPoint,
      awsClientId: this.awsClientId,
      awsRegion: this.awsRegion,
      cdnBaseUrl: this.cdnBaseUrl,
      cloudinaryFolder: this.cloudinaryFolder,
      cognitoPoolName: this.cognitoPoolName,
      domainName: this.domainName,
      leaddynoPublicKey: this.leaddynoPublicKey,
      pusherApiKey: this.pusherApiKey,
      pusherCluster: this.pusherCluster,
      gtmId: this.gtmId,
      mixPanelToken: this.mixPanelToken,
      mixPanelApiHost: this.mixPanelApiHost,
      sentryDsn: this.sentryDsn,
      awsUserPoolDomain: this.awsUserPoolDomain,
      recaptchaSiteKey: this.recaptchaSiteKey,
      isProduction: this.isProduction,
      s3BaseUrl: this.s3BaseUrl,
      gaId: this.gaId,
      paysafePublicKey: this.paysafePublicKey,
      paysafeEnv: this.paysafeEnv,
      featureFlagApiUrl: this.featureFlagApiUrl,
      featureFlagClientKey: this.featureFlagClientKey,
      hubNextBaseUrl: this.hubNextBaseUrl,
      ffCacheInterval: this.ffCacheInterval,
      coralogixRumPublicKey: this.coralogixRumPublicKey,
      coralogixRumDomain: this.coralogixRumDomain,
      coralogixRumInstrumentations: this.coralogixRumInstrumentations,
      version: this.version,
    };
  }

  private isRumInstrumentationEnabled(
    key: EnvKeys,
    defaultEnabled = true,
  ): boolean {
    const fallback = defaultEnabled ? 'true' : 'false';
    return this.getEnvVariable<string>(key, fallback) !== 'false';
  }

  /**
   * Gets a configuration value from environment variables
   * @param key - The key of the environment variable
   * @param fallback - Optional fallback value if the environment variable is not set
   * @returns The environment variable value or the fallback
   */
  private getEnvVariable<T = string>(key: EnvKeys, fallback?: T): T {
    const value = import.meta.env[key];

    if (value == null) {
      console.warn(
        `Environment variable ${key} is not set.${
          fallback !== undefined ? ' Using fallback.' : ''
        }`,
      );
    }

    return (value ?? fallback) as T;
  }
}

export const EnvService = new EnvironmentService();
